import * as React from "react";
import { Mic, MicOff } from "lucide-react";

import { cn } from "../../lib/utils.js";
import { Button } from "./button.js";
import { Tooltip } from "./tooltip.js";

// Minimal typing for the Web Speech API (Chrome, Edge, Safari; not Firefox).
interface SpeechResultLike { isFinal: boolean; 0: { transcript: string } }
interface SpeechEventLike { resultIndex: number; results: ArrayLike<SpeechResultLike> }
interface SpeechRecognitionLike {
  lang: string;
  continuous: boolean;
  interimResults: boolean;
  onresult: ((e: SpeechEventLike) => void) | null;
  onerror: ((e: { error: string }) => void) | null;
  onend: (() => void) | null;
  start(): void;
  stop(): void;
}
type SpeechRecognitionCtor = new () => SpeechRecognitionLike;

function getRecognitionCtor(): SpeechRecognitionCtor | null {
  if (typeof window === "undefined") return null;
  const w = window as unknown as { SpeechRecognition?: SpeechRecognitionCtor; webkitSpeechRecognition?: SpeechRecognitionCtor };
  return w.SpeechRecognition ?? w.webkitSpeechRecognition ?? null;
}

export function isVoiceInputSupported(): boolean {
  return getRecognitionCtor() !== null;
}

export interface VoiceInputButtonProps {
  /** Called with each finished phrase; the caller appends it to its field. */
  onTranscript: (text: string) => void;
  /** Called with the phrase being spoken right now ("" when it is done), for a live preview. */
  onInterim?: (text: string) => void;
  /** Called with a readable message when the microphone can't be used. */
  onError?: (message: string) => void;
  lang?: string;
  /** What the field is, for the button's accessible name ("Dictate description"). */
  label?: string;
  className?: string;
  disabled?: boolean;
}

const ERROR_MESSAGES: Record<string, string> = {
  "not-allowed": "Microphone access was blocked. Allow it in the browser's site settings.",
  "service-not-allowed": "Microphone access was blocked. Allow it in the browser's site settings.",
  "no-speech": "No speech heard. Try again closer to the microphone.",
  "audio-capture": "No microphone found.",
  network: "Voice input needs an internet connection.",
};

/** Mic toggle that dictates into a text field with the browser's speech recognition. */
export function VoiceInputButton({
  onTranscript, onInterim, onError, lang = "en-IN", label = "text", className, disabled,
}: VoiceInputButtonProps) {
  const [listening, setListening] = React.useState(false);
  const recRef = React.useRef<SpeechRecognitionLike | null>(null);
  // Latest callbacks without restarting recognition when the parent re-renders.
  const cbRef = React.useRef({ onTranscript, onInterim, onError });
  cbRef.current = { onTranscript, onInterim, onError };

  React.useEffect(() => () => recRef.current?.stop(), []);

  const Ctor = getRecognitionCtor();
  if (!Ctor) {
    return (
      <Tooltip content="Voice input works in Chrome, Edge or Safari">
        <span className={cn("inline-flex", className)}>
          <Button type="button" variant="ghost" size="sm" disabled aria-label={`Voice input unavailable for ${label}`}>
            <MicOff size={14} />
          </Button>
        </span>
      </Tooltip>
    );
  }

  function start() {
    const rec = new Ctor!();
    rec.lang = lang;
    rec.continuous = true;
    rec.interimResults = true;
    rec.onresult = (e) => {
      let interim = "";
      for (let i = e.resultIndex; i < e.results.length; i++) {
        const r = e.results[i]!;
        const text = r[0].transcript.trim();
        if (r.isFinal) { if (text) cbRef.current.onTranscript(text); }
        else interim += `${text} `;
      }
      cbRef.current.onInterim?.(interim.trim());
    };
    rec.onerror = (e) => {
      if (e.error !== "aborted") cbRef.current.onError?.(ERROR_MESSAGES[e.error] ?? `Voice input stopped (${e.error}).`);
    };
    rec.onend = () => {
      setListening(false);
      cbRef.current.onInterim?.("");
      recRef.current = null;
    };
    recRef.current = rec;
    rec.start();
    setListening(true);
  }

  function toggle() {
    if (recRef.current) recRef.current.stop();
    else start();
  }

  return (
    <Tooltip content={listening ? "Stop dictation" : `Dictate ${label}`}>
      <Button
        type="button"
        variant={listening ? "destructive" : "ghost"}
        size="sm"
        onClick={toggle}
        disabled={disabled}
        aria-pressed={listening}
        aria-label={listening ? "Stop dictation" : `Dictate ${label}`}
        className={cn("gap-1", listening && "animate-pulse", className)}
      >
        <Mic size={14} />
        {listening ? "Listening…" : "Speak"}
      </Button>
    </Tooltip>
  );
}
